import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanagelapi.stechomeyazilim.info";

const ParaCekmeTalepleri = () => {
  document.title = "Para Çekme Talepleri | HarmanaGEL";

  const [talepler, setTalepler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [confirmModal, setConfirmModal] = useState(false);
  const [selectedTalep, setSelectedTalep] = useState(null);
  const [action, setAction] = useState(""); // "onay" | "red"

  const fetchTalepler = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/PARA_CEKME_TALEPLERI?$top=500&$orderby=TALEP_TARIHI desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress,CEP_NO)`
      );
      const result = await response.json();
      if (result && result.value) setTalepler(result.value);
    } catch (err) {
      setError("Talepler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTalepler();
  }, []);

  const handleAction = async () => {
    if (!selectedTalep) return;
    const yeniDurum = action === "onay" ? "Onaylandı" : "Reddedildi";
    try {
      await fetch(`${BASE_URL}/PARA_CEKME_TALEPLERI(${selectedTalep.ID})`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          DURUM: yeniDurum,
          ISLEM_TARIHI: new Date().toISOString(),
        }),
      });
      setSuccessMsg(`Talep ${yeniDurum}.`);
      setConfirmModal(false);
      fetchTalepler();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("İşlem sırasında hata oluştu.");
    }
  };

  const getDurumBadge = (durum) => {
    const renkler = { "Beklemede": "warning", "Onaylandı": "success", "Reddedildi": "danger" };
    return <Badge color={renkler[durum] || "secondary"}>{durum || "Bilinmiyor"}</Badge>;
  };

  const bekleyenCount = useMemo(() => talepler.filter(t => t.DURUM === "Beklemede").length, [talepler]);
  const toplamTutar = useMemo(() => talepler.filter(t => t.DURUM === "Beklemede").reduce((s, t) => s + parseFloat(t.MIKTAR || 0), 0), [talepler]);

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Kullanıcı",
      accessorKey: "KULLANICILAR",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : (
          <div>
            <div className="fw-medium">{cell.row.original.AD_SOYAD}</div>
          </div>
        );
      },
    },
    {
      header: "Miktar",
      accessorKey: "MIKTAR",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => (
        <span className="fw-bold text-success fs-5">
          {parseFloat(cell.getValue() || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺
        </span>
      ),
    },
    {
      header: "IBAN",
      accessorKey: "IBAN",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cell) => <span className="font-monospace small">{cell.getValue()}</span>,
    },
    {
      header: "Durum",
      accessorKey: "DURUM",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => getDurumBadge(cell.getValue()),
    },
    {
      header: "Talep Tarihi",
      accessorKey: "TALEP_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR") : "-";
      },
    },
    {
      header: "İşlem Tarihi",
      accessorKey: "ISLEM_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR") : "-";
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const t = cellProps.row.original;
        if (t.DURUM !== "Beklemede") return null;
        return (
          <div className="d-flex gap-2">
            <Button
              color="success"
              size="sm"
              onClick={() => {
                setSelectedTalep(t);
                setAction("onay");
                setConfirmModal(true);
              }}
            >
              <i className="bx bx-check me-1" />
              Onayla
            </Button>
            <Button
              color="danger"
              size="sm"
              outline
              onClick={() => {
                setSelectedTalep(t);
                setAction("red");
                setConfirmModal(true);
              }}
            >
              <i className="bx bx-x me-1" />
              Reddet
            </Button>
          </div>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Para Çekme Talepleri" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          {bekleyenCount > 0 && (
            <Alert color="warning" className="d-flex align-items-center gap-3">
              <i className="bx bx-time fs-3" />
              <div>
                <strong>{bekleyenCount} bekleyen talep</strong> bulunmaktadır.
                Toplam bekleyen tutar: <strong>{toplamTutar.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</strong>
              </div>
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Para Çekme Talepleri</h4>
                    <Button color="primary" size="sm" onClick={fetchTalepler} disabled={isLoading}>
                      <i className="bx bx-refresh me-1" />
                      Yenile
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={talepler}
                      isGlobalFilter={true}
                      SearchPlaceholder="Talep ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(false)} centered size="sm">
        <ModalHeader toggle={() => setConfirmModal(false)}>
          {action === "onay" ? "Talebi Onayla" : "Talebi Reddet"}
        </ModalHeader>
        <ModalBody>
          {selectedTalep && (
            <div>
              <p>
                <strong>{selectedTalep.AD_SOYAD || selectedTalep.KULLANICILAR?.AD_SOYAD}</strong> tarafından yapılan{" "}
                <strong>{parseFloat(selectedTalep.MIKTAR || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺</strong>{" "}
                tutarındaki para çekme talebini{" "}
                <strong>{action === "onay" ? "onaylamak" : "reddetmek"}</strong> istediğinize emin misiniz?
              </p>
              {action === "onay" && (
                <div className="alert alert-info small">
                  IBAN: <span className="font-monospace">{selectedTalep.IBAN}</span>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setConfirmModal(false)}>İptal</Button>
          <Button color={action === "onay" ? "success" : "danger"} onClick={handleAction}>
            {action === "onay" ? "Onayla" : "Reddet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default ParaCekmeTalepleri;
